import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { api } from './lib/api';
import { applyTheme, readPref, setPref, type ThemePref } from './lib/theme';

const CmsModule    = lazy(() => import('./modules/cms/CmsModule'));
const MailModule   = lazy(() => import('./modules/mail/MailModule'));
const CrmModule    = lazy(() => import('./modules/crm/CrmModule'));
const ConfigModule = lazy(() => import('./modules/config/ConfigModule'));

interface User { id: string; email: string; name: string | null; role: string }

function ThemeToggle() {
  const [pref, setPrefState] = useState<ThemePref>(readPref());
  const cycle = () => {
    const next: ThemePref = pref === 'light' ? 'dark' : pref === 'dark' ? 'auto' : 'light';
    setPref(next); applyTheme(next); setPrefState(next);
  };
  const icon = pref === 'light' ? '☀' : pref === 'dark' ? '🌙' : '🖥';
  return <button className="erp-icon-btn" title={`Tema: ${pref}`} onClick={cycle}>{icon}</button>;
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ user: User }>('/api/core/me').then((d) => setUser(d.user)).catch((e) => setError(String(e)));
  }, []);

  if (error) return <div className="erp-error">No se pudo autenticar: {error}</div>;
  if (!user) return <div className="erp-loading">Cargando…</div>;

  return (
    <BrowserRouter>
      <div className="erp-shell">
        <aside className="erp-sidebar">
          <div className="erp-brand">HOLOTECH<span>ERP</span></div>
          <nav>
            <NavLink to="/cms"    className={({isActive}) => isActive ? 'active' : ''}>CMS</NavLink>
            <NavLink to="/mail"   className={({isActive}) => isActive ? 'active' : ''}>Mail</NavLink>
            <NavLink to="/crm"    className={({isActive}) => isActive ? 'active' : ''}>CRM</NavLink>
            <NavLink to="/config" className={({isActive}) => isActive ? 'active' : ''}>Config</NavLink>
          </nav>
        </aside>
        <div className="erp-main">
          <header className="erp-topbar">
            <input className="erp-search" placeholder="Buscar (próximamente)…" disabled />
            <div className="erp-user">
              <ThemeToggle />
              <span>{user.name ?? user.email}</span>
              <div className="erp-avatar" aria-hidden>{(user.name ?? user.email).charAt(0).toUpperCase()}</div>
            </div>
          </header>
          <main className="erp-content">
            <Suspense fallback={<div className="erp-loading">Cargando módulo…</div>}>
              <Routes>
                <Route path="/" element={<Navigate to="/mail" replace />} />
                <Route path="/cms/*"    element={<CmsModule />} />
                <Route path="/mail/*"   element={<MailModule />} />
                <Route path="/crm/*"    element={<CrmModule />} />
                <Route path="/config/*" element={<ConfigModule />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}
